import React, { useEffect, useState } from "react";
import Image from "next/image";
import { Blur, Remote } from "@/api/loaderRemote";
import Skeleton from "react-loading-skeleton";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation } from "swiper/modules";
import styles from "@/styles/mobileVilla.module.css";
import "react-loading-skeleton/dist/skeleton.css";
import "swiper/css";
import "swiper/css/navigation";
import FancyBox from "./FancyBox";
import ShowMorePhotos from "./ShowMorePhotos";

const VillaGallery = ({ video, images, visibleCount = 5 }) => {
  const [visiblePhotos, setVisiblePhotos] = useState({});

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const filename = entry.target.dataset.index;

            const nextImageIndex =
              images.findIndex((img) => img.Filename === filename) + 1;
            const nextImage = images[nextImageIndex];

            setVisiblePhotos((prev) => ({
              ...prev,
              [filename]: true,
              [nextImage?.Filename]: true,
            }));
          }
        });
      },
      { threshold: 0.5 }
    );

    const imagesElements = document.querySelectorAll("[data-index]");
    imagesElements.forEach((img) => observer.observe(img));

    return () => {
      imagesElements.forEach((img) => {
        try {
          observer.unobserve(img);
        } catch (e) {}
      });
    };
  }, []);

  const coverIamges = images?.slice(0, visibleCount);
  const desktopMoreImages = images?.slice(visibleCount);

  const firstVisiblePhoto = Object.keys(visiblePhotos)[0];
  return (
    <>
      <div className="lg:hidden relative">
        <ShowMorePhotos video={video} images={images} />
        <FancyBox
          options={{
            infinite: false,
            Hash: false,
          }}
          delegate={`[data-fancybox="gallery-mobile"]`}
        >
          <Swiper
            modules={[Navigation]}
            slidesPerView={1}
            spaceBetween={8}
            navigation
            autoplay={{ delay: 5000 }}
            mousewheel="true"
            className={styles.mobileVilla}
          >
            {images?.map((image, index) => {
              return (
                <SwiperSlide key={index}>
                  <div
                    className="w-full h-full"
                    data-fancybox="gallery-mobile"
                    data-src={`${process.env.NEXT_PUBLIC_CDN}/uploads/${image?.Filename}`}
                    data-thumb={`${process.env.NEXT_PUBLIC_CDN}/uploads/150/${image?.Filename}`}
                    data-index={image.Filename}
                    data-width="1920"
                    data-height="1080"
                  >
                    <div className="h-56 sm:h-80 md:h-[464px] relative w-full">
                      {!visiblePhotos[image.Filename] && index !== 0 ? (
                        <div className="relative w-full h-full">
                          <Skeleton height="100%" width="100%" />
                        </div>
                      ) : (
                        <Image
                          src={`${process.env.NEXT_PUBLIC_CDN}/uploads/${image?.Filename}`}
                          alt={image?.Filename}
                          onLoad={() =>
                            setVisiblePhotos((prev) => ({
                              ...prev,
                              [image.Filename]: true,
                            }))
                          }
                          priority={image.Filename === firstVisiblePhoto} // ✅ Priority for first visible
                          loader={Remote}
                          sizes="100vw"
                          fill
                          className="object-cover object-center cursor-pointer"
                        />
                      )}
                    </div>
                  </div>
                </SwiperSlide>
              );
            })}
          </Swiper>
        </FancyBox>
      </div>
      <div className="relative hidden lg:grid lg:grid-cols-4 grid-cols-1 gap-3 lg:mb-[52px] xsm:mb-7 mb-4 h-56 sm:h-[442px]">
        {coverIamges?.map((image, index) => (
          <FancyBox
            key={image.Filename}
            options={{
              infinite: false,
              Hash: false,
            }}
          >
            <div
              data-fancybox="gallery-desktop"
              data-index={image.Filename}
              data-src={`${process.env.NEXT_PUBLIC_CDN}/uploads/${image.Filename}`}
              data-thumb={`${process.env.NEXT_PUBLIC_CDN}/uploads/150/${image.Filename}`}
              className={`relative overflow-hidden rounded-2xl ${
                index === 0 ? "col-span-2 row-span-2" : ""
              }`}
            >
              {!visiblePhotos[image.Filename] && index !== 0 ? (
                <div className="relative w-full h-full">
                  <Skeleton height="100%" width="100%" baseColor="white" />
                </div>
              ) : (
                <Image
                  src={image.Filename}
                  alt={image.Filename}
                  fill
                  loader={Remote}
                  blurDataURL={Blur(image.Filename)}
                  onLoad={() =>
                    setVisiblePhotos((prev) => ({
                      ...prev,
                      [image.Filename]: true,
                    }))
                  }
                  priority={image.Filename === firstVisiblePhoto} // ✅ Priority for first visible
                  sizes={image.Filename === firstVisiblePhoto ? `30vw` : `15vw`}
                  placeholder="blur"
                  className="object-cover object-center transition-transform duration-300 cursor-pointer hover:scale-110"
                />
              )}
              <ShowMorePhotos video={video} images={images} index={index} />
            </div>
          </FancyBox>
        ))}
        <div className="hidden">
          {desktopMoreImages?.map((image, index) => (
            <div
              key={index}
              data-fancybox="gallery-desktop"
              data-src={`${process.env.NEXT_PUBLIC_CDN}/uploads/${image.Filename}`}
              data-thumb={`${process.env.NEXT_PUBLIC_CDN}/uploads/150/${image.Filename}`}
            ></div>
          ))}
        </div>
      </div>
    </>
  );
};

export default VillaGallery;
